import { StepperSelectionEvent } from "@angular/cdk/stepper";
import { AfterViewInit, Component, Inject, OnDestroy, OnInit, Pipe, PipeTransform, ViewChild } from "@angular/core";
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from "@angular/material/dialog";
import { MatSnackBar } from "@angular/material/snack-bar";
import { MatStepper } from "@angular/material/stepper";
import { EncodeObservationsComponent } from "@comunicare/comunicare-angular-observation-encoder";
import { ArrayHelper, EnableWhenHelper, FileLogger, Tools } from "@comunicare/comunicare-helper";
import {
  ACTION_STATUS_ENTITY,
  Answer,
  EnableWhenBehavior,
  IConfiguration,
  IEnableWhen,
  INPUT_TYPE,
  IObservation,
  IObservationDefinition,
  IPatientUser,
  IQuestionnaire,
  IQuestionnaireResponse,
  ISlideContentGroupQuestion,
  ISlideContentQuestion,
  ISlideView,
  PAGING_MODE,
  QRStatus,
  QuestionResponse,
  Reference,
} from "@comunicare/comunicare-interfaces";
import { TranslateService } from "@ngx-translate/core";
import * as moment from "moment";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { QuestionnaireResponse } from "src/app/models/questionnaireResponse.model";
import { ObservationsService } from "src/app/providers/observations.service";
import { PatientService } from "src/app/providers/patient.service";
import { QRService } from "src/app/providers/qr-api.service";
import { SessionService } from "src/app/providers/session.service";

@Pipe({ name: "isQuestionEnabled", pure: false })
// Do not use "pure: false" everywhere as a solution
// this makes the pipe kinda useless since it acts as a normal function
export class IsQuestionEnabledPipe implements PipeTransform {
  transform(question: QuestionResponse, questionnaireResponse: IQuestionnaireResponse): boolean {
    let enableWhens: IEnableWhen[] = question.enableWhen;
    if (!enableWhens) {
      return true;
    } else {
      const behavior = question.enableWhenBehavior ?? EnableWhenBehavior.AND;
      if (!Array.isArray(enableWhens)) {
        // backwards compatibility for old questionnaires
        enableWhens = [enableWhens];
      }

      if (!EnableWhenHelper.isEnableWhen(enableWhens)) {
        FileLogger.warn("IsQuestionEnabledPipe", `it is not an enableWhen (question = ${question.linkId})`, enableWhens);
        return true;
      }

      for (const enableWhen of enableWhens) {
        const values = this.getAnswerValue(enableWhen.question, questionnaireResponse);
        if (!values || values.length < 1 || !values[0]) {
          return false;
        }
        const firstValue = enableWhen.type === "number" ? Number(values[0].valueCoding.code) : values[0].valueCoding.code;
        const isEnabled = EnableWhenHelper.computeIsEnabled(enableWhen, firstValue);
        const behaviorCheck = EnableWhenHelper.checkBehavior(isEnabled, behavior);
        if (Tools.isDefined(behaviorCheck)) {
          return behaviorCheck;
        }
      }
      if (behavior === EnableWhenBehavior.OR) {
        return false;
      }
      if (!Tools.isDefined(behavior) || behavior === EnableWhenBehavior.AND) {
        return true;
      }
    }
  }

  public getAnswerValue(linkId: string, questionnaireResponse: IQuestionnaireResponse): Answer[] {
    if (questionnaireResponse?.group?.question?.length) {
      for (const question of questionnaireResponse.group.question) {
        if (question.linkId === linkId && question?.answer?.length) {
          return question.answer;
        }
      }
    } else if (questionnaireResponse?.group?.group?.length) {
      for (const subGroup of questionnaireResponse.group.group) {
        for (const question of subGroup.question) {
          if (question.linkId === linkId && question?.answer?.length) {
            return question.answer;
          }
        }
      }
    }
    return null;
  }
}
@Component({
  selector: "app-answer-new-qr",
  templateUrl: "./answer-new-qr.component.html",
  styleUrls: ["./answer-new-qr.component.scss"],
})
export class AnswerNewQrComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild("stepper") slides: MatStepper;
  @ViewChild(EncodeObservationsComponent) observationsEncoder: EncodeObservationsComponent;
  public questionnaire: IQuestionnaire;
  public questionnaireResponse: IQuestionnaireResponse;
  public linkedObservationsDef: IObservationDefinition[];
  public linkedObservations: IObservation[] = []; // existing observations linked to the QR (in case of edit)
  public savedObservations: IObservation[] = []; // observations saved while this modal was open
  public slideViews: ISlideView[] = [];
  public isFirst = true;
  public isLast = false;
  public isUpdate = false;
  private onDestroy$ = new Subject<void>();
  public hasValidate = false;
  private newObservations: IObservation[];
  public lang: string;
  public isFullScreen = false;
  public INPUT_TYPE = INPUT_TYPE;
  public lastSavedTime = "";
  public forceExpandObservations = false;
  public forceExpandQuestionnaire = false;
  public saveInProgress = false;
  public autoSaveActivated = false;
  public completionDate = moment().format();
  public maxDate = moment().format();
  public patientConfig: IConfiguration;

  constructor(
    private qrService: QRService,
    private sessionsService: SessionService,
    private snackBar: MatSnackBar,
    private translateService: TranslateService,
    private observationService: ObservationsService,
    public dialog: MatDialog,
    private dialogRef: MatDialogRef<AnswerNewQrComponent>,
    private patientService: PatientService,
    @Inject(MAT_DIALOG_DATA)
    public data: {
      questionnaire: IQuestionnaire;
      patientRef: Reference;
      questionnaireResponse?: QuestionnaireResponse;
      pu: IPatientUser;
    }
  ) {}

  ngOnInit(): void {
    this.patientService
      .getPatientConfiguration(this.data.patientRef.reference)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((config) => {
        this.patientConfig = config;
      });
    this.questionnaire = this.data.questionnaire;
    this.downloadLinkedObservationsDefs();
    // create new response or edit existing one ?
    // build IQuestionnaire Response based on IQuestionnaire template
    if (!this.data.questionnaireResponse) {
      this.questionnaireResponse = QuestionnaireResponse.fromTemplate(this.questionnaire, this.data.patientRef, {
        reference: this.sessionsService.account.caremateIdentifier,
        display: this.sessionsService.account.displayName,
      });
    } else {
      this.questionnaireResponse = this.data.questionnaireResponse;
      this.lang = this.data.questionnaireResponse.version;
      this.isUpdate = true;
      this.downloadLinkedObservations();
      this.completionDate = this.data.questionnaireResponse.authored;
    }
    if (this.questionnaire) {
      this.buildSlideViews();
    }
  }

  ngAfterViewInit(): void {
    if (this.slideViews.length > 1) {
      this.slides.selectionChange.pipe(takeUntil(this.onDestroy$)).subscribe((event: StepperSelectionEvent) => {
        this.isFirst = event.selectedIndex === 0;
        this.isLast = event.selectedIndex === this.slideViews.length - 1;
      });
    }
  }

  ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  private downloadLinkedObservationsDefs(): void {
    const loincs = this.questionnaire?.observationLoincs?.map((o) => o.loinc);
    if (loincs?.length) {
      this.observationService
        .listDef(null, loincs)
        .pipe(takeUntil(this.onDestroy$))
        .subscribe(
          (o: IObservationDefinition[]) => {
            this.linkedObservationsDef = o.filter(ArrayHelper.onlyUniqueLoincObsDef);
            this.linkedObservationsDef.forEach((o) => {
              const matchingLoinc = this.questionnaire?.observationLoincs?.find((loincObj) => loincObj.loinc === o.loinc);
              if (matchingLoinc) {
                o.patientDefaultAccess = matchingLoinc.patientDefaultAccess;
              }
              this.setIsObsRequired(o);
            });
          },
          (err) => {
            FileLogger.error("AnswerNewQRComponent", "Error while downloading observations definitions", err, "none");
            this.linkedObservationsDef = [];
          }
        );
    }
  }

  /**
   * Set the "isRequired" field of the observationDefinition to true if it contains
   * at least one required component
   * @param observationDefinition the observation definition to set
   */
  private setIsObsRequired(observationDefinition: IObservationDefinition): void {
    for (const comp of observationDefinition.components) {
      if (comp.required) {
        observationDefinition.isRequired = true;
        return;
      }
    }
    observationDefinition.isRequired = false;
  }

  private async downloadLinkedObservations(): Promise<void> {
    const questionnaireResponseRef = this.questionnaireResponse?.identifier.value;
    if (questionnaireResponseRef) {
      this.linkedObservations = await this.observationService.getObservationsByQR(questionnaireResponseRef).catch((err) => {
        FileLogger.error("AnswerNewQRComponent", "Error while downloading observations", err, "none");
        return [];
      });
    }
  }

  public linkedObservationsChanged(newObservations: IObservation[]): void {
    this.newObservations = newObservations;
    if (this.autoSaveActivated) this.saveProgress();
  }

  /**
   * Create all slides based on IQuestionnaire
   */
  private buildSlideViews(): void {
    switch (this.questionnaire.paging) {
      default:
      case PAGING_MODE.NONE: {
        // no slides (in fact only 1 slide)
        this.buildSimpleSlideMode();
        break;
      }
      case PAGING_MODE.SINGLE: {
        // 1 slide by question
        this.buildSingleSlideMode();
        break;
      }
      case PAGING_MODE.GROUP: {
        // 1 slide by theme
        this.buildGroupSlideMode();
        break;
      }
    }
  }

  /**
   * build 1 slide by question
   */
  private buildSingleSlideMode(): void {
    // multiple groups of questions
    if (QuestionnaireResponse.isMultiGroup(this.questionnaireResponse)) {
      // loop on sub groups
      for (let i = 0; i < this.questionnaire.group.group.length; i++) {
        const subGroup = this.questionnaire.group.group[i];
        // loop on questions for each group
        for (let j = 0; j < subGroup.question.length; j++) {
          const question = subGroup.question[j];
          // one slide for 1 content group
          const slideView: ISlideView = {
            identifier: this.questionnaireResponse.identifier.value,
            content: [],
          };
          // one content group for 1 question
          const slideContentGroup: ISlideContentGroupQuestion = {
            title: subGroup.title,
            description: subGroup.text,
            questionResponses: [],
          };
          const slideContent: ISlideContentQuestion = {
            question,
            response: this.questionnaireResponse.group.group[i].question[j],
          };
          // set question/response to its Content Group
          slideContentGroup.questionResponses = [slideContent];
          // add content group to its slide
          slideView.content = [slideContentGroup];
          // add slide to the list of slides
          this.slideViews.push(slideView);
        }
      }
    } else {
      // flat questionnaires (only main group with questions)
      // loop on main group questions
      for (let i = 0; i < this.questionnaire.group.question.length; i++) {
        const question = this.questionnaire.group.question[i];
        // one slide for 1 content group
        const slideView: ISlideView = {
          identifier: this.questionnaireResponse.identifier.value,
          content: [],
        };
        // one content group for 1 question
        const slideContentGroup: ISlideContentGroupQuestion = {
          title: this.questionnaire.group.title,
          description: this.questionnaire.group.text,
          questionResponses: [],
        };
        const slideContent: ISlideContentQuestion = {
          question,
          response: this.questionnaireResponse.group.question[i],
        };
        // set question/response to its Content Group
        slideContentGroup.questionResponses = [slideContent];
        // add content group to its slide
        slideView.content = [slideContentGroup];
        // add slide to the list of slides
        this.slideViews.push(slideView);
      }
    }
    // console.text("buildSingleSlideMode", this.slideViews);
  }

  /**
   * build 1 slide by theme (by group)
   */
  private buildGroupSlideMode(): void {
    // multiple groups of questions
    if (QuestionnaireResponse.isMultiGroup(this.questionnaireResponse)) {
      // loop on sub groups
      for (let i = 0; i < this.questionnaire.group.group.length; i++) {
        const subGroup = this.questionnaire.group.group[i];
        // one slide for 1 group
        const slideView: ISlideView = {
          identifier: this.questionnaireResponse.identifier.value,
          content: [],
        };
        // one content group for all questions of this group
        const slideContentGroup: ISlideContentGroupQuestion = {
          title: subGroup.title,
          description: subGroup.text,
          questionResponses: [],
        };
        // add 1 content group to its slide
        slideView.content = [slideContentGroup];
        // loop on questions for each group
        for (let j = 0; j < subGroup.question.length; j++) {
          const question = subGroup.question[j];
          const slideContent: ISlideContentQuestion = {
            question,
            response: this.questionnaireResponse.group.group[i].question[j],
          };
          // add question/response to its Content Group
          slideContentGroup.questionResponses.push(slideContent);
        }
        // add slide to the list of slides
        this.slideViews.push(slideView);
      }
    } else {
      // flat questionnaires (only main group with questions)
      // ??? not possible to build by group mode if there are no groups (only main) ==> same as simple mode
      this.buildSimpleSlideMode();
    }
  }

  /**
   * No slide ==> build 1 single slide with all questions
   */
  private buildSimpleSlideMode(): void {
    const slideContentGroups: ISlideContentGroupQuestion[] = [];
    // multiple groups of questions
    if (QuestionnaireResponse.isMultiGroup(this.questionnaireResponse)) {
      // loop on sub groups
      for (let i = 0; i < this.questionnaire.group.group.length; i++) {
        const subGroup = this.questionnaire.group.group[i];
        const slideContentGroup: ISlideContentGroupQuestion = {
          title: subGroup.title,
          description: subGroup.text,
          questionResponses: [],
        };
        // loop on questions for each sub groups
        for (let j = 0; j < subGroup.question.length; j++) {
          const question = subGroup.question[j];
          const slideContent: ISlideContentQuestion = {
            question,
            response: this.questionnaireResponse.group.group[i].question[j],
          };
          slideContentGroup.questionResponses.push(slideContent);
        }
        slideContentGroups.push(slideContentGroup);
      }
    } else {
      // flat questionnaires (only main group with questions)
      const slideContentGroup: ISlideContentGroupQuestion = {
        title: this.questionnaire.group.title,
        description: this.questionnaire.group.text,
        questionResponses: [],
      };
      // loop on main group questions
      for (let i = 0; i < this.questionnaire.group.question.length; i++) {
        const question = this.questionnaire.group.question[i];
        const slideContent: ISlideContentQuestion = {
          question,
          response: this.questionnaireResponse.group.question[i],
        };
        // add question/response to the single Content Group
        slideContentGroup.questionResponses.push(slideContent);
      }
      slideContentGroups.push(slideContentGroup);
    }
    // create 1 slide
    const slideView: ISlideView = {
      identifier: this.questionnaireResponse.identifier.value,
      content: slideContentGroups,
    };
    // add only 1 slide to the list of slides
    this.slideViews = [slideView];
  }

  public async saveProgress(): Promise<void> {
    if (this.saveInProgress) return;
    this.saveInProgress = true;

    this.questionnaireResponse.status = QRStatus.IN_PROGRESS;
    try {
      await this.save();
      this.lastSavedTime = moment().format("HH:mm");
      this.isUpdate = true;
    } catch (err) {
      FileLogger.error("AnswerNewQR", "Error while saving QR progress", err, "none");
    }
    this.saveInProgress = false;
  }

  public async complete(): Promise<void> {
    if (this.saveInProgress) return;
    this.saveInProgress = true;

    this.questionnaireResponse.status = QRStatus.COMPLETED;

    if (this.isComplete()) {
      try {
        this.deleteEmptyResponses();
        await this.save();
        this.close();
      } catch (err) {
        FileLogger.error("AnswerNewQR", "Error while completing QR", err, "none");
      }
    } else {
      this.hasValidate = true;
    }
    this.saveInProgress = false;
  }

  private async save(): Promise<void> {
    this.observationsEncoder?.save();
    this.questionnaireResponse.modified = moment().format();
    this.questionnaireResponse.questionnairePublicationDate = this.questionnaire.date;
    this.questionnaireResponse.actionStatus = this.isUpdate ? ACTION_STATUS_ENTITY.MODIFIED : ACTION_STATUS_ENTITY.CREATED;
    if (this.questionnaire.completionDateEditable) {
      this.questionnaireResponse.authored = this.completionDate ?? moment().format();
      this.newObservations?.map((o) => (o.issued = this.completionDate ?? moment().format()));
    }
    if (this.isUpdate) {
      // Remove empty new observations (keep empty pre-existing observations, the server will have to delete them):
      this.setObservationsIds(this.newObservations);
      const filteredObs = this.newObservations?.filter(
        (o) =>
          o._id ||
          !!o.component.find(
            (c) => Tools.isDefined(c.valueQuantity?.value) || c.valueQuantity?.valueArray?.length || c?.valuePictures?.length
          )
      );
      const result = await this.qrService.updateQR(this.questionnaireResponse, filteredObs);
      this.savedObservations = result.createdObs.concat(result.updatedObs);
    } else {
      // Remove empty observations:
      const filteredObs = this.newObservations?.filter(
        (o) =>
          !!o.component.find(
            (c) => Tools.isDefined(c.valueQuantity?.value) || c.valueQuantity?.valueArray?.length || c?.valuePictures?.length
          )
      );
      const result = await this.qrService.createQR(this.questionnaireResponse, filteredObs).toPromise();
      const obsCreated = result.createdObservations;
      this.savedObservations = obsCreated;
    }
    this.sessionsService.needRefresQRDataList();
    this.sessionsService.needRefreshObservationsList();
  }

  private setObservationsIds(newObs: IObservation[]): void {
    if (!this.savedObservations?.length || !newObs?.length) {
      // first time saving the QR while this modal is open, nothing to do
      return;
    }
    for (const obs of newObs) {
      // find the observations that were created while this modal is open
      // and set their _id
      const alreadyCreated = this.savedObservations.find((o) => o.code.coding[0].code === obs.code.coding[0].code);
      if (alreadyCreated) obs._id = alreadyCreated._id;
    }
  }

  private close(): void {
    this.snackBar.open(this.translateService.instant("common.saveSuccess"), undefined, {
      duration: 3000,
      horizontalPosition: "center",
      verticalPosition: "top",
    });
    this.dialog.closeAll();
  }
  public onInputChange(): void {
    if (this.autoSaveActivated) this.saveProgress();
  }

  /**
   * check if current vue is complete
   */
  private isComplete(): boolean {
    this.forceExpandQuestionnaire = false;
    this.forceExpandObservations = false;
    // get the view
    const index = this.slides?.selectedIndex ?? 0;
    const currentView = this.slideViews[index];
    if (currentView) {
      //  if exist take the content
      const currentQuestionnaire = currentView.content[0] as ISlideContentGroupQuestion;
      if (currentQuestionnaire.questionResponses) {
        // if exist loop on all Questions and response
        for (const qr of currentQuestionnaire.questionResponses) {
          // if question is required, enabled and response empty, return false
          const isQuestionEnabledtype = new IsQuestionEnabledPipe();
          const isQuestionEnabled = isQuestionEnabledtype.transform(qr.response, this.questionnaireResponse);
          if (qr.question.required && qr.response.answer.length === 0 && isQuestionEnabled) {
            this.forceExpandQuestionnaire = true;
            return false;
          }
        }
      } else {
        return this.isObsComplete();
      }
    } else {
      this.forceExpandQuestionnaire = true;
      return false;
    }
    return this.isObsComplete();
  }

  private isObsComplete(): boolean {
    const observationsValid = this.observationsEncoder ? this.observationsEncoder.checkValidity() : true;
    if (!observationsValid) {
      this.forceExpandObservations = true;
      return false;
    }
    return true;
  }

  private deleteEmptyResponses(): void {
    const index = this.slides?.selectedIndex ?? 0;
    const currentView = this.slideViews[index];
    if (currentView) {
      //  if exist take the content
      const currentQuestionnaire = currentView.content[0] as ISlideContentGroupQuestion;
      if (currentQuestionnaire.questionResponses) {
        // if exist loop on all Questions and response
        for (const qr of currentQuestionnaire.questionResponses) {
          // Delete empty responses
          if (qr.response.answer && !qr.response.answer[0]) {
            delete qr.response;
          }
        }
      }
    }
  }

  public onResizeModal(): void {
    this.isFullScreen = !this.isFullScreen;
    if (this.isFullScreen) {
      this.dialogRef.updateSize("100vw", "100vh");
    } else {
      this.dialogRef.updateSize("70vw", "80vh");
    }
  }
}
